import omit from 'lodash/omit';
import moment from 'moment';
import { loadFromLocalStorage } from 'UI/utils';

import { STATIC_RANGES } from '../constants';

export const cleanFilters = filters => {
  if (!filters) return {};

  return Object.fromEntries(
    Object.entries(filters).map(([key, value]) => {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const cleanedInner = Object.fromEntries(
          Object.entries(value).filter(
            ([_, innerValue]) => innerValue !== undefined && innerValue !== null
          )
        );
        return [key, cleanedInner];
      }
      return [key, value];
    })
  );
};

export const areDateRangesEqual = (range1, range2) => {
  const hasRange1 = !!(range1?.startDate && range1?.endDate);
  const hasRange2 = !!(range2?.startDate && range2?.endDate);

  if (!hasRange1 && !hasRange2) return true;
  if (!hasRange1 || !hasRange2) return false;

  const { startDate: startDate1, endDate: endDate1 } = range1;
  const { startDate: startDate2, endDate: endDate2 } = range2;

  return moment(startDate1).isSame(startDate2, 'day') && moment(endDate1).isSame(endDate2, 'day');
};

export const sanitizeSavedSearch = savedSearch => {
  if (!savedSearch) return null;

  return {
    ...omit(savedSearch, ['ownerId', 'ownerName']),
    owner: {
      id: savedSearch.ownerId,
      name: savedSearch.ownerName
    },
    search: omit(savedSearch?.search, [
      'dateTypeFilter',
      'endDate',
      'isFromStaticPeriod',
      'startDate',
      'staticDateRangeId'
    ])
  };
};

export const buildPeriodFromSearch = search => {
  if (!search) return null;
  const { dateTypeFilter, endDate, startDate, isFromStaticPeriod, staticDateRangeId } =
    search || {};
  const staticRanges = getStaticRanges();
  const matchedStaticDateRange =
    isFromStaticPeriod && staticDateRangeId
      ? staticRanges.find(staticRange => staticRange.id === staticDateRangeId)
      : null;

  if (!matchedStaticDateRange && !startDate && !endDate) return null;

  return {
    dateTypeFilter,
    endDate: matchedStaticDateRange?.endDate || endDate,
    startDate: matchedStaticDateRange?.startDate || startDate,
    isFromStaticPeriod,
    staticDateRangeId
  };
};

export const getActiveSearchFromPreferences = key => {
  if (!key) return null;

  const { savedSearch = null } = loadFromLocalStorage(key) || {};
  if (!savedSearch) return null;

  return {
    savedSearch: sanitizeSavedSearch(savedSearch),
    savedPeriod: buildPeriodFromSearch(savedSearch?.search)
  };
};

export const saveActiveSearchPreferences = (key, data) => {
  if (!key) return;

  const previous = loadFromLocalStorage(key);
  if (!previous) return;

  const newPreferences = {
    ...previous,
    filters: data?.search || {},
    savedSearch: data
  };

  localStorage.setItem(key, JSON.stringify(newPreferences));
};

export const isSameRangeByDay = (a, b) => {
  if (!a || !b) return false;

  return (
    moment(a.startDate).isSame(b.startDate, 'day') && moment(a.endDate).isSame(b.endDate, 'day')
  );
};

export const getStaticRanges = () => {
  const parsedStaticRanges = STATIC_RANGES.map(staticRange => ({
    ...staticRange,
    startDate: moment(staticRange.startDate).toDate(),
    endDate: moment(staticRange.endDate).toDate()
  }));
  return parsedStaticRanges;
};

export const getMatchedStaticDateRange = currentSearch => {
  const hasDateRangeFilter = currentSearch?.startDate || currentSearch?.endDate;

  if (!hasDateRangeFilter || !currentSearch?.isFromStaticPeriod) return null;

  const staticRanges = getStaticRanges();
  return staticRanges.find(staticRange => isSameRangeByDay(currentSearch, staticRange)) || null;
};
